import React from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { youtubeEmbedUrl } from "../../landingEditor/youtube";
import { normalizeCalendlyUrl } from "../../../../utils/calendly";
import { registryFieldError } from "../inspector/inspectorModel";
import { getValueAtDescriptorPath } from "../elementEditing";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { VisualColorControl } from "../inspector/VisualSettingControls";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";


function StyleField({ field, form, disabled, onCommit }) {
  const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
  if (field.type === SEGMENT_FIELD_TYPES.COLOR) {
    return <VisualColorControl label={field.label} value={String(value ?? "")} fallback={field.defaultValue || "transparent"} disabled={disabled} allowExactCss onChange={(next) => onCommit?.(field.path, next)} onReset={() => onCommit?.(field.path, field.defaultValue)} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.NUMBER) {
    return <TextField fullWidth size="small" type="number" label={field.label} value={value ?? ""} disabled={disabled} inputProps={{ min: field.limits?.min, max: field.limits?.max, step: field.limits?.step }} onChange={(event) => onCommit?.(field.path, event.target.value === "" ? "" : Number(event.target.value))} />;
  }
  if (field.type === SEGMENT_FIELD_TYPES.SELECT) {
    return <FormControl fullWidth size="small" disabled={disabled}><InputLabel>{field.label}</InputLabel><Select label={field.label} value={value ?? ""} onChange={(event) => onCommit?.(field.path, event.target.value)}>{(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}</Select></FormControl>;
  }
  return null;
}

export default function CustomEmbedElementEditor({ descriptor, form, disabled, onCommit }) {
  const field = descriptor?.field;
  const path = descriptor?.fieldPath || field?.path || "";
  const mode = descriptor?.editorMode === "reservation" ? "reservation" : "video";
  const current = String(getValueAtDescriptorPath(form, path, "") || "");
  const [draft, setDraft] = React.useState(current);

  React.useEffect(() => setDraft(current), [current, path]);
  if (!field || !path) return null;

  const calendlyUrl = mode === "reservation" ? normalizeCalendlyUrl(draft) : "";
  const youtubeUrl = mode === "video" ? youtubeEmbedUrl(draft) : "";
  const validationError = mode === "reservation"
    ? registryFieldError(field, draft)
    : (draft.trim() && !youtubeUrl ? "Pegá un enlace válido de YouTube." : "");
  const configured = Boolean(draft.trim()) && !validationError;
  const styleFields = Array.isArray(descriptor?.styleFields) ? descriptor.styleFields : [];

  const commit = () => {
    if (draft === current || validationError) return;
    onCommit?.(path, draft);
  };

  return (
    <Stack spacing={1.3}>
      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
        <Chip size="small" label={mode === "video" ? "Video" : "Reserva"} />
        <Chip
          size="small"
          variant={configured ? "filled" : "outlined"}
          label={configured ? "Configurado" : draft.trim() ? "Revisar URL" : "Sin configurar"}
        />
      </Stack>

      <TextField
        fullWidth
        size="small"
        label={field.label || (mode === "video" ? "Video de YouTube" : "Enlace de Calendly")}
        value={draft}
        disabled={disabled}
        error={Boolean(validationError)}
        helperText={validationError || (mode === "reservation"
          ? "Usá el enlace del evento que querés mostrar en esta sección."
          : "Acepta enlaces normales, Shorts, Live y youtu.be.")}
        inputProps={{ maxLength: field.maxLength || 2000 }}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />

      {mode === "video" && youtubeUrl ? (
        <Box
          component="iframe"
          src={youtubeUrl}
          title="Vista previa del video"
          sx={{ width: "100%", aspectRatio: "16 / 9", border: 0, borderRadius: 2.5, bgcolor: "#000" }}
        />
      ) : null}

      {mode === "reservation" && calendlyUrl ? (
        <Box sx={{ p: 1.2, border: "1px solid rgba(25,36,47,.1)", borderRadius: 2.5, bgcolor: "rgba(25,36,47,.025)" }}>
          <Typography variant="caption" sx={{ display: "block", fontWeight: 900, color: "rgba(25,36,47,.6)" }}>Reserva lista</Typography>
          <Typography variant="body2" sx={{ mt: 0.35, overflowWrap: "anywhere", color: "#19242F" }}>{calendlyUrl}</Typography>
        </Box>
      ) : null}
      {styleFields.length ? (
        <Accordion disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.1)", borderRadius: "12px !important", "&::before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}><Typography variant="body2" sx={{ fontWeight: 900 }}>Apariencia</Typography></AccordionSummary>
          <AccordionDetails><Stack spacing={1}>{styleFields.map((field) => <StyleField key={field.path} field={field} form={form} disabled={disabled} onCommit={onCommit} />)}</Stack></AccordionDetails>
        </Accordion>
      ) : null}
    </Stack>
  );
}
